import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import Navbar from './components/Navbar';
import BottomNavbar from './components/BottomNavbar';
import AppRoutes from './routes/AppRoutes';
import './App.css';
import { ToastContainer, toast } from 'react-toastify';
import { socketService } from './services';
import NewOrderModal from './modal/NewOrderModal';
import { Order } from './utils/types';
import { RestaurantProvider, useRestaurant } from './context/RestaurantContext';
import { NotificationProvider, useNotifications } from './context/NotificationContext';

function App() {
  const [loginStatus, setLoginStatus] = useState<boolean>(false);
  const [isNewOrderModalOpen, setIsNewOrderModalOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);

  // Check login status on mount and listen for storage changes
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('kitchenAuthToken');
      setLoginStatus(!!token);
    };

    // Initial check
    checkLoginStatus();

    // Listen for storage changes (login/logout in other tabs)
    window.addEventListener('storage', checkLoginStatus);

    return () => {
      window.removeEventListener('storage', checkLoginStatus);
    };
  }, []);

  // Handle new order from socket
  const handleNewOrder = (order: Order) => {
    console.log('[App] New order received:', order);
    // Store the order data
    setCurrentOrder(order);
    // Open the modal
    setIsNewOrderModalOpen(true);
    // Show toast notification
    toast.info('New order received!', {
      position: 'top-right',
      autoClose: 3000,
    });
  };

  // Socket connection management
  useEffect(() => {
    if (loginStatus) {
      socketService.connect();
      socketService.on('newOrder', handleNewOrder);
    } else {
      socketService.disconnect();
    }

    return () => {
      socketService.off('newOrder', handleNewOrder);
    };
  }, [loginStatus]);

  const handleAcceptOrder = () => {
    console.log('[App] Order accepted');
    setIsNewOrderModalOpen(false);
    setCurrentOrder(null);
  };

  const handleRejectOrder = () => {
    console.log('[App] Order rejected');
    setIsNewOrderModalOpen(false);
    setCurrentOrder(null);
  };

  const handleCloseModal = () => {
    setIsNewOrderModalOpen(false);
    setCurrentOrder(null);
  };

  return (
    <Router>
      <RestaurantProvider>
        <NotificationProvider>
          <AppContent 
            loginStatus={loginStatus}
            isNewOrderModalOpen={isNewOrderModalOpen}
            currentOrder={currentOrder}
            handleAcceptOrder={handleAcceptOrder}
            handleRejectOrder={handleRejectOrder}
            handleCloseModal={handleCloseModal}
          />
        </NotificationProvider>
      </RestaurantProvider>
    </Router>
  );
}

const AppContent: React.FC<any> = ({
  isNewOrderModalOpen,
  currentOrder,
  handleAcceptOrder,
  handleRejectOrder,
  handleCloseModal
}) => {
  const { isRestaurantOpen } = useRestaurant();
  const { refreshUnreadCount } = useNotifications();

  useEffect(() => {
    if (isNewOrderModalOpen) {
      refreshUnreadCount();
    }
  }, [isNewOrderModalOpen, refreshUnreadCount]);

  return (
    <>
      <ToastContainer />
      <div className="App bg-white dark:bg-gray-900 min-h-screen transition-colors">
        {isRestaurantOpen && <Navbar />}
        <main className="main-content bg-white dark:bg-gray-900">
          <AppRoutes />
        </main>
        
        {/* Bottom Navigation for Mobile */}
        {isRestaurantOpen && <BottomNavbar />}
          
        {/* Global New Order Modal */}
        <NewOrderModal
            isOpen={isNewOrderModalOpen}
            order={currentOrder}
            onClose={handleCloseModal}
            onAccept={handleAcceptOrder}
            onReject={handleRejectOrder}
          />
        </div>
      </>
  );
}

export default App;
